import { useMemo } from 'react';

import {
  collectParamsFilterValues,
  collectCountriesSlugs,
  createCitiesForFilter,
  updateFiltersState,
  defineSelectedValueTitle,
  filterCitiesByCountryParam,
} from '../../utils/filters';

import { ESTATE_FILTER_KEYS } from '../../constants';

import { SelectItem } from '../../types';

import { SearchList } from '../SearchList';
import { SelectWithSearch } from '../SelectWithSearch';

import { FilterStub } from './CountryFilter';

import type { SpecialFilterProps } from './CountryFilter';
import type { StrapiCity, StrapiCountry } from '@monoimin/sb-types';

type CityFilterProps = SpecialFilterProps & {
  cities: Array<StrapiCity>;
  countries: Array<StrapiCountry>;
};

export const CityFilter = ({
  cities,
  countries,
  filtersState,
  onChange,
  classNames,
  onClick,
  realEstateDict,
  variant,
}: CityFilterProps) => {
  const filteredCities = useMemo(() => {
    return filterCitiesByCountryParam(
      cities,
      filtersState?.[ESTATE_FILTER_KEYS.COUNTRY]
    );
  }, [cities, filtersState]);

  const citiesForFilter = useMemo(() => {
    return createCitiesForFilter(filteredCities);
  }, [filteredCities]);

  const citiesValue = useMemo(() => {
    return collectParamsFilterValues(citiesForFilter, filtersState?.city);
  }, [citiesForFilter, filtersState?.city]);

  const citiesValueTitle = useMemo(() => {
    return defineSelectedValueTitle(citiesValue, realEstateDict.city);
  }, [citiesValue, realEstateDict.city]);

  const onFilterChange = (value: SelectItem | Array<SelectItem>) => {
    if (typeof onChange !== 'function') {
      return;
    }

    const countriesSlugs = collectCountriesSlugs(countries, cities, value);

    const currentСountriesSlugs = filtersState?.[ESTATE_FILTER_KEYS.COUNTRY];

    if (!Array.isArray(currentСountriesSlugs)) {
      return;
    }

    const newFiltersState = updateFiltersState({
      key: ESTATE_FILTER_KEYS.CITY,
      selectedData: value,
      filtersState: {
        ...filtersState,
        [ESTATE_FILTER_KEYS.COUNTRY]: [
          ...new Set([...countriesSlugs, ...currentСountriesSlugs]),
        ],
      },
    });

    onChange(newFiltersState);
  };

  if (variant === 'mobile-stub') {
    return (
      <FilterStub
        title={citiesValueTitle}
        classNames={classNames}
        onClick={onClick}
      />
    );
  }

  if (variant === 'mobile') {
    return (
      <SearchList
        data={citiesForFilter}
        value={citiesValue}
        onChange={onFilterChange}
        placeholder={realEstateDict.city}
        realEstateDict={realEstateDict}
      />
    );
  }

  return (
    <SelectWithSearch
      data={citiesForFilter}
      placeholder={realEstateDict.allCities}
      value={citiesValue}
      onChange={onFilterChange}
      searchConfig={{
        placeholder: realEstateDict.findCity,
      }}
      classNames={classNames}
      realEstateDict={realEstateDict}
      variant='column'
    />
  );
};
